/**
 * Delivery Countdown Component
 * Shows a countdown timer for order cutoff and estimated delivery date
 */

if (!customElements.get("delivery-countdown")) {
  class DeliveryCountdown extends HTMLElement {
    constructor() {
      super();
      this.cutoffHour = parseInt(this.dataset.cutoffHour) || 14;
      this.processingDays = parseInt(this.dataset.processingDays) || 2;
      this.shippingDays = parseInt(this.dataset.shippingDays) || 3;
      this.intervalId = null;
    }

    connectedCallback() {
      this.hoursEl = this.querySelector(".delivery-countdown__hours");
      this.minutesEl = this.querySelector(".delivery-countdown__minutes");
      this.secondsEl = this.querySelector(".delivery-countdown__seconds");
      this.dateEl = this.querySelector(".delivery-countdown__date");

      if (this.hoursEl && this.minutesEl && this.secondsEl && this.dateEl) {
        this.updateCountdown();
        this.intervalId = setInterval(() => this.updateCountdown(), 1000);
      }
    }

    disconnectedCallback() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    }

    updateCountdown() {
      const now = new Date();
      const cutoff = this.getCutoffTime(now);
      const timeRemaining = cutoff - now;

      if (timeRemaining <= 0) {
        // Cutoff passed, show next day's cutoff
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextCutoff = this.getCutoffTime(tomorrow);
        this.displayTime(nextCutoff - now);
        this.displayDeliveryDate(tomorrow);
      } else {
        this.displayTime(timeRemaining);
        this.displayDeliveryDate(now);
      }
    }

    getCutoffTime(date) {
      const cutoff = new Date(date);
      cutoff.setHours(this.cutoffHour, 0, 0, 0);
      return cutoff;
    }

    displayTime(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.hoursEl.textContent = this.padZero(hours);
      this.minutesEl.textContent = this.padZero(minutes);
      this.secondsEl.textContent = this.padZero(seconds);
    }

    displayDeliveryDate(orderDate) {
      // Calculate delivery date: order date + processing + shipping (business days)
      const deliveryDate = this.addBusinessDays(
        orderDate,
        this.processingDays + this.shippingDays
      );

      // Format: "Thursday 29th January" (EN) or "jeudi 29 janvier" (FR)
      const lang = document.documentElement.lang || "en";
      const weekday = deliveryDate.toLocaleDateString(lang, { weekday: "long" });
      const month = deliveryDate.toLocaleDateString(lang, { month: "long" });
      const day = deliveryDate.getDate();

      // Only add ordinal suffix for English
      const isEnglish = lang.startsWith("en");
      const suffix = isEnglish ? this.getOrdinalSuffix(day) : "";

      this.dateEl.textContent = `${weekday} ${day}${suffix} ${month}`;
    }

    addBusinessDays(date, days) {
      const result = new Date(date);
      let addedDays = 0;

      while (addedDays < days) {
        result.setDate(result.getDate() + 1);
        const dayOfWeek = result.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          addedDays++;
        }
      }

      return result;
    }

    getOrdinalSuffix(day) {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    }

    padZero(num) {
      return num.toString().padStart(2, "0");
    }
  }

  customElements.define("delivery-countdown", DeliveryCountdown);
}
